// ==========================================================================
// screens/PropertyLedger.tsx — the rental ledger on a property page (FF-036 part 2)
//   Upload the agent's statement → Read it (the reader suggests every line)
//   → check the lines → Approve. Or type a line in. Lines already in the
//   ledger are flagged and skipped; the agent's payouts are matched to the
//   bank deposits so the rent is only counted once.
// ==========================================================================
import { useEffect, useMemo, useState, type DragEvent } from "react";
import { format, parseISO } from "date-fns";
import { AlertTriangle, CheckCircle2, FileText, FileUp, Link2, Plus, Sparkles, Trash2 } from "lucide-react";
import { Card, ErrorText } from "@/components/ui";
import Sheet, { Lbl, inputCls } from "@/components/Sheet";
import { money, useAccounts, useCategories, type Category } from "@/lib/money";
import { useWorkerFile, useWorkerHealth } from "@/lib/receipts";
import {
  LEDGER_KINDS, draftsFrom, existingFingerprints, fingerprints, kindLabel, readStatement, signFor,
  useAddLedgerLine, useAddStatements, useApproveStatement, useArchiveStatement, useLinkPayouts, usePayoutMatches, useRentalStatements,
  type DraftLine, type LedgerKind, type PayoutMatch, type RentalStatement, type StatementRead,
} from "@/lib/rental";

const d = (s: string | null) => (s ? format(parseISO(s), "d MMM yyyy") : "—");
const dollars = (c: number) => (Math.abs(c) / 100).toFixed(2);
const toCents = (v: string) => Math.round(Math.abs(Number(v.replace(/[$,\s]/g, "")) || 0) * 100);

/** Budget codes for a ledger line: the property's own codes first. */
function CodeSelect({ value, cats, onChange, disabled }: { value: string | null; cats: Category[]; onChange: (id: string | null) => void; disabled?: boolean }) {
  const live = cats.filter((c) => !c.is_group && !c.archived_at);
  const g800 = cats.find((g) => g.is_group && g.code === "800")?.id;
  const mine = live.filter((c) => c.code === "140" || c.parent_id === g800).sort((a, b) => a.code.localeCompare(b.code));
  const rest = live.filter((c) => !mine.includes(c)).sort((a, b) => a.code.localeCompare(b.code));
  return (
    <select value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value || null)} className={`${inputCls} py-1.5 text-sm`}>
      <option value="">{disabled ? "Money movement (945)" : "Pick a code"}</option>
      <optgroup label="Property">{mine.map((c) => <option key={c.id} value={c.id}>{c.code} {c.name}</option>)}</optgroup>
      <optgroup label="Everything else">{rest.map((c) => <option key={c.id} value={c.id}>{c.code} {c.name}</option>)}</optgroup>
    </select>
  );
}

function PayoutLinks({ matches, code945, compact }: { matches: PayoutMatch[]; code945: string | undefined; compact?: boolean }) {
  const accounts = useAccounts(); const link = useLinkPayouts();
  if (!matches.length || !code945) return null;
  const clash = matches.filter((m) => m.state === "coded_other").length;
  return (
    <div className={`space-y-2 rounded-lg p-4 text-sm ${clash ? "bg-warn/10" : "bg-primary/10"}`}>
      <div className={`flex items-start gap-2 font-medium ${clash ? "text-warn" : "text-primary"}`}>
        {clash ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> : <Link2 className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>
          {matches.length} of the agent's payments to us {matches.length === 1 ? "is" : "are"} in the bank.
          {clash ? ` ${clash === 1 ? "One is" : `${clash} are`} coded as something else (usually 140 Rent), so the rent would count twice.` : ""}
          {" "}Link {matches.length === 1 ? "it" : "them"}: the bank line becomes 945 "Rent paid out by agent", a money movement, and the rent is counted once, from the statement.
        </span>
      </div>
      {!compact && (
        <ul className="space-y-1 pl-6 text-ink">
          {matches.map((m) => (
            <li key={m.payout.id} className="flex flex-wrap justify-between gap-2">
              <span>{d(m.deposit.posted_on)} · {accounts.data?.find((a) => a.id === m.deposit.account_id)?.name} · {m.deposit.description.slice(0, 40)}</span>
              <span className="num">{money(m.deposit.amount_cents, { cents: true })} <span className="text-xs text-ink-muted">{m.state === "unreconciled" ? "not reconciled yet" : m.state === "is945" ? "already 945" : "coded as something else"}</span></span>
            </li>
          ))}
        </ul>
      )}
      <button type="button" disabled={link.isPending} onClick={() => link.mutate({ matches, code945 })} className="rounded-full bg-primary px-4 py-1.5 font-semibold text-white disabled:opacity-50">
        {link.isPending ? "Linking…" : `Link ${matches.length === 1 ? "it" : `all ${matches.length}`}`}
      </button>
      <ErrorText>{link.error ? (link.error as Error).message : null}</ErrorText>
    </div>
  );
}

// ---------------------------------------------------------------------------
export function RentalLedgerCard({ assetId, name }: { assetId: string; name: string }) {
  const health = useWorkerHealth(); const statements = useRentalStatements(assetId); const add = useAddStatements(); const archive = useArchiveStatement();
  const cats = useCategories(); const code945 = cats.data?.find((c) => c.code === "945" && !c.is_group)?.id;
  const matches = usePayoutMatches(assetId, code945);
  const [drag, setDrag] = useState(false); const [open, setOpen] = useState<string | null>(null); const [adding, setAdding] = useState(false);
  const workerUp = !!health.data?.ok;
  const take = (files: FileList | null) => { if (files?.length) add.mutate({ assetId, files: Array.from(files) }, { onSuccess: (ids) => { if (ids.length === 1) setOpen(ids[0]); } }); };
  const list = statements.data ?? [];
  const openS = list.find((s) => s.id === open);

  return (
    <Card className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">Rental ledger</h2>
          <p className="text-sm text-ink-muted">The agent's statements: gross rent, fees and repairs from the trust account. Upload one and the reader fills in the lines, or type a line in. Anything already in the ledger is skipped.</p>
        </div>
        <button type="button" onClick={() => setAdding(true)} className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium ring-1 ring-line"><Plus className="h-4 w-4" /> Add a line</button>
      </div>

      <PayoutLinks matches={matches.data ?? []} code945={code945} />

      {health.data && !workerUp && (
        <div className="flex gap-3 rounded-lg bg-warn/10 p-3 text-sm text-warn"><AlertTriangle className="h-5 w-5 shrink-0" /><div>The file service isn't running. In PowerShell at C:\dev\family-app run <code className="rounded bg-surface px-1">pnpm dev:worker</code>, then refresh. Typing a line in still works.</div></div>
      )}
      <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e: DragEvent) => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files); }}
        className={`flex cursor-pointer flex-col items-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-6 text-center transition ${drag ? "border-primary bg-primary/5" : "border-line hover:border-primary/50"} ${!workerUp ? "pointer-events-none opacity-50" : ""}`}>
        <FileUp className="h-6 w-6 text-primary" />
        <span className="font-medium">{add.isPending ? "Uploading…" : `Drop ${name}'s agent statement here, or click to choose`}</span>
        <span className="text-xs text-ink-muted">PDF or a photo. Monthly or end-of-year statements both work.</span>
        <input type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { take(e.target.files); e.target.value = ""; }} />
      </label>
      <ErrorText>{add.error ? (add.error as Error).message : null}</ErrorText>

      {list.length > 0 && (
        <ul className="divide-y divide-line/60">
          {list.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
              <FileText className="h-5 w-5 shrink-0 text-ink-muted" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{s.statement_no ? `Statement ${s.statement_no}` : s.file_name ?? "Statement"}</div>
                <div className="text-xs text-ink-muted">
                  {s.period_from || s.period_to ? `${d(s.period_from)} – ${d(s.period_to)}` : `Added ${d(s.created_at.slice(0, 10))}`}
                  {" · "}{s.approved_at ? `Approved: ${s.lines_added ?? 0} added${s.lines_skipped ? `, ${s.lines_skipped} already there` : ""}` : s.ai_read_at ? "Read, waiting for your check" : s.uploaded ? "Not read yet" : "Upload didn't finish"}
                </div>
              </div>
              {s.approved_at ? <span className="flex items-center gap-1 text-xs text-good"><CheckCircle2 className="h-4 w-4" /> In the ledger</span> : null}
              <button type="button" onClick={() => setOpen(s.id)} className={`rounded-full px-3.5 py-1.5 text-sm font-medium ${s.approved_at ? "ring-1 ring-line" : "bg-primary text-white"}`}>{s.approved_at ? "Open" : s.ai_read_at ? "Check and approve" : "Open"}</button>
              {!s.approved_at && <button type="button" title="Remove" aria-label="Remove" onClick={() => archive.mutate(s.id)} className="rounded-full p-1.5 text-ink-muted hover:text-bad"><Trash2 className="h-4 w-4" /></button>}
            </li>
          ))}
        </ul>
      )}
      {openS && <StatementSheet key={openS.id} s={openS} readerOn={!!health.data?.reader} cats={cats.data ?? []} code945={code945} onClose={() => setOpen(null)} />}
      {adding && <AddLineSheet assetId={assetId} cats={cats.data ?? []} onClose={() => setAdding(false)} />}
    </Card>
  );
}

// ---------------------------------------------------------------------------
function StatementSheet({ s, readerOn, cats, code945, onClose }: { s: RentalStatement; readerOn: boolean; cats: Category[]; code945: string | undefined; onClose: () => void }) {
  const file = useWorkerFile(s.uploaded ? `/statements/${s.id}/file` : null);
  const approve = useApproveStatement(); const matches = usePayoutMatches(s.asset_id, code945);
  const [read, setRead] = useState<StatementRead | null>(s.ai_suggested);
  const [lines, setLines] = useState<DraftLine[]>(() => (s.ai_suggested ? draftsFrom(s.ai_suggested, cats) : []));
  const [from, setFrom] = useState(s.period_from ?? s.ai_suggested?.period_from ?? ""); const [to, setTo] = useState(s.period_to ?? s.ai_suggested?.period_to ?? "");
  const [no, setNo] = useState(s.statement_no ?? s.ai_suggested?.statement_no ?? "");
  const [reading, setReading] = useState(false); const [err, setErr] = useState("");
  const [have, setHave] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<{ added: number; skipped: number } | null>(null);

  // Which lines are already in the ledger (same date, same amount).
  const fps = useMemo(() => fingerprints(lines.filter((l) => l.include)), [lines]);
  useEffect(() => {
    let alive = true;
    existingFingerprints(s.asset_id, lines.map((l) => l.entry_date).filter((x) => /^\d{4}-\d{2}-\d{2}$/.test(x)))
      .then((m) => { if (alive) setHave(new Set(m.keys())); }).catch(() => undefined);
    return () => { alive = false; };
  }, [s.asset_id, lines.map((l) => l.entry_date).join(","), done]); // eslint-disable-line react-hooks/exhaustive-deps
  const included = lines.filter((l) => l.include);
  const dupOf = (l: DraftLine) => { const i = included.indexOf(l); return i >= 0 && have.has(fps[i]); };

  async function doRead() {
    setReading(true); setErr("");
    try { const r = await readStatement(s.id); setRead(r); setLines(draftsFrom(r, cats)); setFrom(r.period_from ?? ""); setTo(r.period_to ?? ""); setNo(r.statement_no ?? ""); }
    catch (e) { setErr((e as Error).message); }
    finally { setReading(false); }
  }
  const set = (k: string, patch: Partial<DraftLine>) => setLines((ls) => ls.map((l) => (l.key === k ? { ...l, ...patch } : l)));

  // Our totals against the statement's own summary.
  const sum = (f: (l: DraftLine) => boolean) => included.filter(f).reduce((t, l) => t + l.amount_cents, 0);
  const ours = {
    incoming: sum((l) => l.amount_cents > 0),
    fees: -sum((l) => l.kind === "fee"),
    expenses: -sum((l) => l.kind === "expense" || (l.kind === "other" && l.amount_cents < 0)),
    paid_to_owner: -sum((l) => l.kind === "payout"),
  };
  const theirs = read?.totals ?? {};
  const checks = (["incoming", "fees", "expenses", "paid_to_owner"] as const).filter((k) => typeof theirs[k] === "number")
    .map((k) => ({ k, label: { incoming: "Money in", fees: "Agent fees", expenses: "Expenses", paid_to_owner: "Paid to us" }[k], ours: ours[k], theirs: Math.round((theirs[k] as number) * 100) }));
  const newCount = included.filter((l) => !dupOf(l)).length;

  return (
    <Sheet title={s.statement_no ? `Statement ${s.statement_no}` : "Agent statement"} onClose={onClose} wide>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.5fr)]">
        <div className="lg:sticky lg:top-0 lg:self-start">
          {file.url ? (s.mime === "application/pdf" ? <iframe title="Statement" src={file.url} className="h-64 w-full rounded-md ring-1 ring-line lg:h-[70vh]" /> : <img src={file.url} alt="Statement" className="max-h-64 w-full rounded-md object-contain ring-1 ring-line lg:max-h-[70vh]" />)
            : <div className="flex h-60 items-center justify-center rounded-md bg-surface-raised text-sm text-ink-muted">{file.err ? "Couldn't load the file." : s.uploaded ? "Loading…" : "No file."}</div>}
        </div>
        <div className="min-w-0 space-y-4">
          {!read && !s.approved_at && (
            <div className="space-y-2 rounded-lg bg-primary/10 p-4 text-sm">
              <p>The reader can fill in every line for you to check. Nothing goes into the ledger until you press Approve.</p>
              <button type="button" disabled={!readerOn || reading || !s.uploaded} onClick={doRead} className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"><Sparkles className="h-4 w-4" /> {reading ? "Reading… (up to a minute)" : "Read it"}</button>
              {!readerOn && <p className="text-xs text-ink-muted">The reader is off (no key in the Worker). Use Add a line instead.</p>}
            </div>
          )}
          <ErrorText>{err || null}</ErrorText>

          {done && (
            <div className="flex items-start gap-2 rounded-lg bg-good/10 p-3 text-sm text-good"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{done.added} line{done.added === 1 ? "" : "s"} added to the ledger{done.skipped ? `, ${done.skipped} skipped because ${done.skipped === 1 ? "it was" : "they were"} already there` : ""}.</span></div>
          )}
          {done && <PayoutLinks matches={(matches.data ?? [])} code945={code945} compact />}

          {lines.length > 0 && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Lbl label="From"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} /></Lbl>
                <Lbl label="To"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} /></Lbl>
                <Lbl label="Statement no."><input value={no} onChange={(e) => setNo(e.target.value)} className={inputCls} /></Lbl>
              </div>

              <div className="space-y-2">
                {lines.map((l) => {
                  const dup = l.include && dupOf(l);
                  return (
                    <div key={l.key} className={`space-y-2 rounded-md p-3 ring-1 ${!l.include ? "opacity-50 ring-line" : dup ? "bg-surface-raised ring-line" : "ring-line"}`}>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={l.include} onChange={(e) => set(l.key, { include: e.target.checked })} className="h-4 w-4 accent-primary" aria-label="Include" />
                        <input value={l.description} onChange={(e) => set(l.key, { description: e.target.value })} className={`${inputCls} py-1.5 text-sm`} />
                        <span className={`num shrink-0 text-sm font-semibold ${l.amount_cents > 0 ? "text-good" : ""}`}>{money(l.amount_cents, { cents: true })}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-[8.5rem_7.5rem_minmax(0,1fr)_6.5rem]">
                        <input type="date" value={l.entry_date} onChange={(e) => set(l.key, { entry_date: e.target.value })} className={`${inputCls} py-1.5 text-sm`} />
                        <select value={l.kind} onChange={(e) => { const k = e.target.value as LedgerKind; set(l.key, { kind: k, amount_cents: signFor(k, l.amount_cents), category_id: k === "payout" ? null : l.category_id }); }} className={`${inputCls} py-1.5 text-sm`}>
                          {LEDGER_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                        </select>
                        <CodeSelect value={l.category_id} cats={cats} disabled={l.kind === "payout"} onChange={(id) => set(l.key, { category_id: id })} />
                        <div className="flex items-center gap-1">
                          {l.kind === "other" && <button type="button" title="Money in or out" onClick={() => set(l.key, { amount_cents: -l.amount_cents })} className="rounded px-1.5 py-1 text-xs ring-1 ring-line">{l.amount_cents > 0 ? "In" : "Out"}</button>}
                          <input key={`${l.key}${l.amount_cents}`} inputMode="decimal" defaultValue={dollars(l.amount_cents)} aria-label="Amount"
                            onBlur={(e) => { const c = toCents(e.target.value); set(l.key, { amount_cents: l.kind === "other" ? (Math.sign(l.amount_cents) || -1) * c : signFor(l.kind, c) }); }}
                            className={`${inputCls} num py-1.5 text-right text-sm`} />
                        </div>
                      </div>
                      {dup && <p className="text-xs text-ink-muted">Already in the ledger (same date and amount), so it will be skipped.</p>}
                    </div>
                  );
                })}
              </div>

              {checks.length > 0 && (
                <div className="rounded-md bg-surface-raised p-3 text-sm">
                  <div className="mb-1 font-medium">Against the statement's own totals</div>
                  {checks.map((c) => (
                    <div key={c.k} className="flex justify-between gap-2">
                      <span>{c.label}</span>
                      <span className={`num ${c.ours === c.theirs ? "text-good" : "text-warn"}`}>{money(c.ours, { cents: true })} {c.ours === c.theirs ? "✓" : `(statement says ${money(c.theirs, { cents: true })})`}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <button type="button" disabled={approve.isPending || included.length === 0} onClick={() => approve.mutate({ statement: s, lines, period_from: from || null, period_to: to || null, statement_no: no || null }, { onSuccess: setDone })}
                  className="rounded-full bg-primary px-5 py-2.5 font-semibold text-white disabled:opacity-50">
                  {approve.isPending ? "Saving…" : s.approved_at ? `Approve again (${newCount} new)` : `Approve ${newCount} line${newCount === 1 ? "" : "s"}`}
                </button>
                {readerOn && !reading && <button type="button" onClick={doRead} className="text-sm text-ink-muted underline">Read it again</button>}
                <span className="text-xs text-ink-muted">Payouts aren't income: they're matched to the bank deposit.</span>
              </div>
              <ErrorText>{approve.error ? (approve.error as Error).message : null}</ErrorText>
            </>
          )}
        </div>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
function AddLineSheet({ assetId, cats, onClose }: { assetId: string; cats: Category[]; onClose: () => void }) {
  const add = useAddLedgerLine();
  const code = (c: string) => cats.find((x) => !x.is_group && x.code === c)?.id ?? null;
  const [kind, setKind] = useState<LedgerKind>("rent"); const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [amount, setAmount] = useState(""); const [inOut, setInOut] = useState<1 | -1>(-1);
  const [cat, setCat] = useState<string | null>(code("140")); const [desc, setDesc] = useState(""); const [gst, setGst] = useState("");
  const cents = kind === "other" ? inOut * toCents(amount) : signFor(kind, toCents(amount));
  const pick = (k: LedgerKind) => { setKind(k); setCat(k === "payout" ? null : k === "rent" ? code("140") : k === "fee" ? code("810") : k === "expense" ? code("830") : cat); };
  return (
    <Sheet title="Add a ledger line" onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); add.mutate({ assetId, line: { key: "m", include: true, entry_date: date, kind, category_id: cat, amount_cents: cents, gst_cents: gst ? toCents(gst) : null, description: desc } }, { onSuccess: onClose }); }}>
        <Lbl label="What is it">
          <div className="flex flex-wrap gap-1.5">{LEDGER_KINDS.map((k) => <button key={k.value} type="button" title={k.hint} onClick={() => pick(k.value)} className={`rounded-full px-3 py-1.5 text-sm ${kind === k.value ? "bg-ink font-semibold text-bg" : "ring-1 ring-line"}`}>{k.label}</button>)}</div>
        </Lbl>
        <p className="text-xs text-ink-muted">{LEDGER_KINDS.find((k) => k.value === kind)?.hint}.{kind === "payout" ? " It's matched to the bank deposit, not counted as income." : ""}</p>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="Date"><input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></Lbl>
          <Lbl label="Amount ($)">
            <div className="flex gap-1.5">
              {kind === "other" && <button type="button" onClick={() => setInOut(inOut === 1 ? -1 : 1)} className="rounded-md px-2 text-sm ring-1 ring-line">{inOut === 1 ? "In" : "Out"}</button>}
              <input inputMode="decimal" required value={amount} onChange={(e) => setAmount(e.target.value)} className={`${inputCls} num`} placeholder="0.00" />
            </div>
          </Lbl>
        </div>
        <Lbl label="Description"><input required value={desc} onChange={(e) => setDesc(e.target.value)} className={inputCls} placeholder="e.g. Rent 1 Oct – 14 Oct" /></Lbl>
        {kind !== "payout" && <Lbl label="Budget code"><CodeSelect value={cat} cats={cats} onChange={setCat} /></Lbl>}
        {(kind === "fee" || kind === "expense") && <Lbl label="GST included ($)" hint="Optional"><input inputMode="decimal" value={gst} onChange={(e) => setGst(e.target.value)} className={`${inputCls} num`} /></Lbl>}
        <div className="rounded-md bg-surface-raised p-3 text-sm">Goes in as <span className={`num font-semibold ${cents > 0 ? "text-good" : ""}`}>{money(cents, { cents: true })}</span> · {kindLabel(kind)}</div>
        <ErrorText>{add.error ? (add.error as Error).message : null}</ErrorText>
        <button type="submit" disabled={add.isPending} className="w-full rounded-full bg-primary px-5 py-3 font-semibold text-white disabled:opacity-50">{add.isPending ? "Saving…" : "Add to the ledger"}</button>
      </form>
    </Sheet>
  );
}
